import * as tvx from "../lib/tvx-plugin-module.min";
import {
  ACTIVE_USER_DATA,
  callCallback,
  coloredText,
  getShowingUserAccount,
  IS_LOGIN,
  isLogin,
  objToBase64,
  validateAccount,
  validateAccountAsync,
  validateAccountForProtectedAction,
  validateAccountForProtectedActionAsync,
  forceValidateAccountOnAction,
  startAccountValidationTimer,
  stopAccountValidationTimer,
} from "../utils";

import { createHomeRoot } from "./home/home";
import { createLoginRoot, getInputEmail, getInputPassword, clearLoginInputs } from "./login";
import { createMovieRoot as createMovieRoot } from "./fictions/movie";
import { updateStudiosIcons } from "./home/studios-row";
import {
  getDestMovieByCollectionId,
  updateCollectionRow,
} from "./collections-row";
import { updateTVPage, updateTVCountry, updateTVCountryCategory, backToCountriesMenu, getTVViewMode, getTVCurrentCountry, getTVCurrentCategory } from "./tv";
import {
  getDestFictionByRootFictionId,
  updateFictionAlsoLikesRow,
} from "./fictions/fiction";
import { createSerialRoot, setSerialSeason } from "./fictions/serial";
import {
  res_backgroundJPG,
  res_blackColor,
  res_check_internet_headline,
  res_check_internet,
  res_coming_soon,
  res_season,
  res_sign_in,
} from "../static-res";
import { createMoviesRoot } from "./movies";
import { createSerialsRoot } from "./serials";
import { createCollectionRoot } from "./collection";
import { createCollectionsRoot } from "./collections";
import { createSearchRoot } from "./search";
import { authLogin } from "../api/worker";
import { apiAuthRoot } from "../api/api-models";
import {
  createChartsRoot,
  getDestFictionByChartId,
  updateChartRow,
} from "./charts";
import { sendPlayerAnalytics } from "../analytics";
import { sendSessionAnalytics } from "../analytics";
import { 
  createWatchTimeTracker, 
  setGlobalWatchTimeTracker, 
  cleanupGlobalWatchTimeTracker,
  setupBeforeUnloadHandler 
} from "../watch-time-tracker";
import { createTVRoot } from "./tv";
import { getRegionByDomain, res_search_background, res_search_for, res_more, SERVER_ADDRESS, studioIdMap } from "../static-res";
import { getSearchResult, getStudioContent } from "../api/worker";
import { apiGetMainStudios } from "../api/routes";

const STORAGE_PREFIX_ID: string = "screenify:";
// Track current top-level content id (e.g., "home", "tv", "movies")
let currentContentId: string = "home";
export function getCurrentContentId(): string {
  return currentContentId;
}

// Helper function to stop any playing live video
function stopLiveVideo(): void {
  console.log("=== STOP LIVE VIDEO CALLED ===");
  
  try {
    // Use TVX VideoPlugin API - same as for movies
    if (typeof tvx !== 'undefined' && tvx.VideoPlugin) {
      try {
        console.log("Stopping live video via TVX VideoPlugin API (same as movies)");
        tvx.VideoPlugin.stopPlayback();
        tvx.VideoPlugin.cancelPlayback();
        tvx.VideoPlugin.executeAction("player:eject");
        console.log("Live video stopped via TVX API");
      } catch (e) {
        console.log("Error stopping via TVX VideoPlugin API:", e);
      }
    } else {
      console.log("TVX VideoPlugin not available");
    }
  } catch (e) {
    console.log("Error in stopLiveVideo:", e);
  }
}
export const playerProperties = {
  "button:content:display": "false",
  "button:speed:icon": "settings",
  "button:speed:enable": "false",
  "trigger:back": "player:commit:message:end",
  "button:speed:action": "panel:request:player:settings:main",
  "trigger:complete": "player:commit:message:complete",
};
export function reloadContent(focusIndex?: boolean): void {
  console.log("reload:content" + (focusIndex === true ? ">item:index" : ""));

  tvx.InteractionPlugin.executeAction(
    "reload:content" + (focusIndex === true ? ">item:index" : "")
  );
}

export async function loadContent(
  contentId: string,
  callback: (data: any) => void
): Promise<void> {
  console.log("=== LOAD CONTENT CALLED ===");
  console.log("ContentId:", contentId);
  
  // Исключаем проверку для страницы логина
  if (contentId === "sign-in") {
    console.log("Sign-in page detected, skipping account validation");
  } else {
    // Принудительная проверка валидности аккаунта перед загрузкой контента
    const isValid = await forceValidateAccountOnAction();
    if (!isValid) {
      console.log("Account validation failed, stopping content load");
      return;
    }
  }

  // Update current top-level content id for global back handling
  if (typeof contentId === "string" && contentId.length > 0) {
    // Normalize cases like "request:interaction:content:tv" → "tv"
    if (contentId === "tv" || contentId.endsWith(":tv") || contentId.indexOf(":content:tv") >= 0) {
      currentContentId = "tv";
    } else if (contentId === "home") {
      currentContentId = "home";
    } else if (contentId === "movies") {
      currentContentId = "movies";
    } else if (contentId === "serials") {
      currentContentId = "serials";
    } else if (contentId === "collections") {
      currentContentId = "collections";
    } else if (contentId === "charts") {
      currentContentId = "charts";
    } else if (contentId === "search") {
      currentContentId = "search";
    } else {
      const delimIndex = contentId.indexOf(":");
      currentContentId = delimIndex > 0 ? contentId.substring(0, delimIndex) : contentId;
    }
  }

  tvx.InteractionPlugin.onValidatedSettings((infoData: tvx.MSXAttachedInfo) => {
    if (tvx.Tools.isFullStr(contentId)) {
      if (contentId == "home") {
        createHomeRoot(callback);
      } else if (contentId == "movies") {
        createMoviesRoot(callback);
      } else if (contentId == "serials") {
        createSerialsRoot(callback);
      } else if (contentId == "charts") {
        createChartsRoot(callback);
      } else if (contentId == "collections") {
        createCollectionsRoot(callback);
      } else if (contentId == "search") {
        createSearchRoot(callback);
      } else if (contentId == "tv") {
        createTVRoot(callback);
      } else if (contentId == "sign-in") {
        callCallback(createLoginRoot(), callback);
      } else if (contentId.indexOf("studio:") == 0) {
        const studioName = contentId.substring(7);
        createStudioRoot(studioName, callback);
      } else if (contentId.indexOf("fiction:") == 0) {
        const fictionAction = contentId.substring(8);
        if (fictionAction.indexOf("also:") == 0) {
          const alsoLikeFictionAction = fictionAction.substring(5);
          if (alsoLikeFictionAction.indexOf("movie:") == 0) {
            const movieAction = alsoLikeFictionAction.substring(6).split(":");
            const rootMovieId = movieAction[0];
            const destMovieIndex = movieAction[1];
            const movie = getDestFictionByRootFictionId(
              rootMovieId,
              destMovieIndex
            );
            createMovieRoot(movie.imdbid, callback);
          } else {
            const serialAction = alsoLikeFictionAction.substring(7).split(":");
            const rootSerialId = serialAction[0];
            const destSerialIndex = serialAction[1];

            const serial = getDestFictionByRootFictionId(
              rootSerialId,
              destSerialIndex
            );
            console.log(serial);

            createSerialRoot(serial.imdbid, callback);
          }
        } else if (fictionAction.indexOf("collection:") == 0) {
          const collectionFictionAction = fictionAction
            .substring(11)
            .split(":");

          const collectionId = collectionFictionAction[0];
          const fictionIndex = collectionFictionAction[1];
          const fiction = getDestMovieByCollectionId(
            collectionId,
            fictionIndex
          );
          if (fiction.fiction.kind == "movie") {
            createMovieRoot(fiction.fiction.movie.imdbid, callback);
          } else {
            createSerialRoot(fiction.fiction.serial.imdbid, callback);
          }
        } else if (fictionAction.indexOf("charts:") == 0) {
          const chartsFictionAction = fictionAction.substring(7).split(":");

          const countryId = chartsFictionAction[0];
          const fictionIndex = chartsFictionAction[1];
          const fiction = getDestFictionByChartId(countryId, fictionIndex);
          if (fiction.fiction.kind == "movie") {
            createMovieRoot(fiction.fiction.movie.imdbid, callback);
          } else {
            createSerialRoot(fiction.fiction.serial.imdbid, callback);
          }
        } else if (fictionAction.indexOf("movie:") == 0) {
          const movieId = fictionAction.substring(6);
          createMovieRoot(movieId, callback);
        } else if (fictionAction.indexOf("serial:") == 0) {
          const serialId = fictionAction.substring(7);
          createSerialRoot(serialId, callback);
        }
      } else if (contentId.indexOf("collection:") == 0) {
        const collectionId = contentId.substring(11);
        createCollectionRoot(collectionId, callback);
      } else {
        callCallback(comingSoonRoot(), callback);
      }
    } else {
      callCallback(createContentLoadError(contentId, "Missing ID"), callback);
    }
  });
}

export async function executeContent(action: string, data: any): Promise<void> {
  console.log("=== EXECUTE CONTENT CALLED ===");
  console.log("Action:", action);
  
  // Исключаем проверку для действий аутентификации
  if (action.indexOf("sign-in:") === 0) {
    console.log("Authentication action detected, skipping account validation");
  } else {
    // Принудительная проверка валидности аккаунта перед каждым действием
    const isValid = await forceValidateAccountOnAction();
    if (!isValid) {
      console.log("Account validation failed, stopping action execution");
      return;
    }
  }
  
  if (tvx.Tools.isFullStr(action)) {
    if (action.indexOf("home:") == 0) {
      // HOME PAGE ACTIONS
      tvx.InteractionPlugin.startLoading();
      let extendAction: string = action.substring(5);
      if (extendAction.indexOf("studios:") == 0) {
        let studiosAction: string = extendAction.substring(8);
        if (studiosAction.indexOf("click:") == 0) {
          const studioName = studiosAction.substring(6);
          console.log("Studio clicked:", studioName);
          // Открываем страницу фильмов с фильтром по студии
          tvx.InteractionPlugin.executeAction(`content:request:interaction:content:studio:${studioName}`);
        } else {
          switch (studiosAction) {
            case "back": {
              updateStudiosIcons(-1);
              break;
            }
            case "forward": {
              updateStudiosIcons(1);
              break;
            }
          }
        }
      } else {
        console.log("EXTENDED ACTION: " + extendAction);

        tvx.InteractionPlugin.stopLoading();
      }
    } else if (action.indexOf("sign-in:") == 0) {
      // SIGN IN PAGE ACTIONS

      let loginAction: string = action.substring(8);
      if (loginAction.indexOf("auth:") == 0) {
        const authAction = loginAction.substring(5);
        // Используем localStorage напрямую вместо tvx.Services.storage

        if (authAction == "in") {
          const username = getInputEmail();
          const password = getInputPassword();
          
          // Валидация на клиенте
          if (!username || !password || username.trim() === '' || password.trim() === '') {
            tvx.InteractionPlugin.error("Please enter both email and password");
            tvx.InteractionPlugin.stopLoading();
            return;
          }
          
          // Простая валидация email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(username.trim())) {
            tvx.InteractionPlugin.error("Please enter a valid email address");
            tvx.InteractionPlugin.stopLoading();
            return;
          }
          
          console.log("Starting login process for:", username);
          tvx.InteractionPlugin.startLoading();
          
          authLogin(username, password).then((result) => {
            console.log("Login result:", result);

            if (result === false) {
              tvx.InteractionPlugin.error("Wrong email or password. Please try again.");
              tvx.InteractionPlugin.stopLoading();
            } else if (result && typeof result === 'object' && result.user && result.accessToken) {
              // Успешный логин
              localStorage.setItem(STORAGE_PREFIX_ID + IS_LOGIN, "true");
              const userData = objToBase64(result.user);
              localStorage.setItem(STORAGE_PREFIX_ID + ACTIVE_USER_DATA, userData);
              localStorage.setItem(STORAGE_PREFIX_ID + "accessToken", result.accessToken);
              
              console.log("Login successful, user data saved");
              tvx.InteractionPlugin.info(`Welcome, ${result.user.userName || result.user.email}!`);
              tvx.InteractionPlugin.executeAction("update:menu:sign-in", {
                label: getShowingUserAccount(),
                icon: "account-circle",
              });
              tvx.InteractionPlugin.executeAction("reload:content");
              // Открываем главное меню после успешного логина.
              // Для совместимости добавляем ссылку на текущий index.html как в start.json
              const initUrl = (typeof window !== 'undefined' && window.location)
                ? `${window.location.origin}/index.html`
                : "index.html";
              tvx.InteractionPlugin.executeAction(`menu:request:interaction:init@${initUrl}`);
              sendSessionAnalytics();
              
              // Запускаем таймер проверки валидности аккаунта
              startAccountValidationTimer();
              
              tvx.InteractionPlugin.stopLoading();
            } else {
              console.error("Unexpected login result format:", result);
              tvx.InteractionPlugin.error("Login failed. Please try again.");
              tvx.InteractionPlugin.stopLoading();
            }
          }).catch((error) => {
            console.error("Login process error:", error);
            tvx.InteractionPlugin.error("Login failed. Please check your connection and try again.");
            tvx.InteractionPlugin.stopLoading();
          });
        } else if (authAction == "out") {
          console.log("Logging out user");
          
          // Останавливаем таймер проверки аккаунта
          stopAccountValidationTimer();
          
          localStorage.setItem(STORAGE_PREFIX_ID + IS_LOGIN, "false");
          localStorage.setItem(STORAGE_PREFIX_ID + ACTIVE_USER_DATA, "");
          localStorage.setItem(STORAGE_PREFIX_ID + "accessToken", "");
          clearLoginInputs(); // Очищаем поля ввода
          tvx.InteractionPlugin.executeAction("update:menu:sign-in", {
            label: res_sign_in(),
            icon: "login",
          });
          tvx.InteractionPlugin.executeAction("reload:content");
          tvx.InteractionPlugin.info("You have been logged out");
          tvx.InteractionPlugin.stopLoading();
        }
      } else {
        console.log("EXTENDED ACTION: " + loginAction);
        tvx.InteractionPlugin.stopLoading();
      }
    } else if (action.indexOf("fiction:") == 0) {
      let extendAction: string = action.substring(8);
      if (extendAction.indexOf("also_like:") == 0) {
        let alsoLikeRowAction: string = extendAction.substring(10);
        if (alsoLikeRowAction.indexOf("back:") == 0) {
          const fictionId = alsoLikeRowAction.substring(5);
          updateFictionAlsoLikesRow(fictionId, -1);
        } else {
          const fictionId = alsoLikeRowAction.substring(8);
          updateFictionAlsoLikesRow(fictionId, 1);
        }
      }
    } else if (action.indexOf("serial:") == 0) {
      let extendAction: string = action.substring(7);
      if (extendAction.indexOf("seasons:") == 0) {
        const data = extendAction.substring(8).split(":");
        const serialId = data[0];
        const seasonNumber = data[1];
        setSerialSeason(serialId, seasonNumber);
        tvx.InteractionPlugin.executeAction(
          "update:content:serial-seasons-btn",
          {
            title: coloredText(res_blackColor, `${res_season()} ${seasonNumber}`),
          }
        );
        tvx.InteractionPlugin.executeAction("reload:content");
      }
    } else if (action.indexOf("fictions:") == 0) {
      let extendAction: string = action.substring(9);
      if (extendAction.indexOf("collections:") == 0) {
        let collectionsAction: string = extendAction.substring(12);
        if (collectionsAction.indexOf("back:") == 0) {
          const collectionId = collectionsAction.substring(5);
          updateCollectionRow(collectionId, -1);
        } else {
          const collectionId = collectionsAction.substring(8);
          updateCollectionRow(collectionId, 1);
        }
      } else if (extendAction.indexOf("charts:") == 0) {
        let chartsAction: string = extendAction.substring(7);
        if (chartsAction.indexOf("back:") == 0) {
          const chartId = chartsAction.substring(5);
          updateChartRow(chartId, -1);
        } else {
          const chartId = chartsAction.substring(8);
          updateChartRow(chartId, 1);
        }
      }
    } else if (action.indexOf("tv:") == 0) {
      // TV PAGE ACTIONS
      tvx.InteractionPlugin.startLoading();
      let tvAction: string = action.substring(3);
      if (tvAction == "back") {
        console.log("=== TV BACK ACTION TRIGGERED ===");
        // Stop any playing video when navigating back in TV
        stopLiveVideo();
        
        const currentMode = getTVViewMode();
        const currentCountry = getTVCurrentCountry();
        const currentCategory = getTVCurrentCategory();
        console.log("TV back action - current mode:", currentMode, "country:", currentCountry, "category:", currentCategory);
        // If we're viewing channels of a specific country+category, go back to countries list
        if (currentMode === "channels" && (currentCountry !== "all" || currentCategory !== "all")) {
          console.log("Going back to countries menu");
          backToCountriesMenu();
        } else {
          console.log("Going to previous page");
          // Otherwise, just go to previous page
          updateTVPage(-1);
        }
      } else if (tvAction == "forward") {
        // Stop any playing video when navigating forward
        stopLiveVideo();
        updateTVPage(1);
      } else if (tvAction == "back-to-countries") {
        // Stop any playing video when going back to countries
        stopLiveVideo();
        backToCountriesMenu();
      } else if (tvAction.indexOf("country-category:") == 0) {
        const parts = tvAction.substring(17).split(":");
        if (parts.length >= 2) {
          const country = decodeURIComponent(parts[0]);
          const category = decodeURIComponent(parts[1]);
          console.log("Decoded country:", country, "category:", category);
          updateTVCountryCategory(country, category);
        }
      } else if (tvAction.indexOf("country:") == 0) {
        const country = tvAction.substring(8);
        updateTVCountry(country);
      }
    }
  } else {
    tvx.InteractionPlugin.warn("Empty content action");
  }
}

export async function loadVideo(
  fictionId: string,
  callback: (data: any) => void
): Promise<void> {
  console.log("=== LOAD VIDEO CALLED ===");
  console.log("FictionId:", fictionId);
  
  // Проверяем валидность аккаунта перед воспроизведением видео (защищенное действие)
  const isValid = await forceValidateAccountOnAction();
  if (!isValid) {
    console.log("Account validation failed for video playback, stopping video load");
    return;
  }
  
  // Дополнительная проверка через API для критических действий
  try {
    const { validateTokenWithAPI } = await import("../utils");
    const apiValid = await validateTokenWithAPI();
    if (!apiValid) {
      console.log("API validation failed for video playback, stopping video load");
      return;
    }
  } catch (error) {
    console.log("API validation error for video playback, allowing playback:", error.message);
    // Не блокируем воспроизведение при ошибках API
  }
  
  if (fictionId.indexOf("watch:") == 0) {
    const watchAction = fictionId.substring(6).split("|");
    const type = watchAction[0];
    const url = watchAction[1];
    const title = watchAction[2];
    const image = watchAction[3] ? watchAction[3].replace("$", "@") : "";
    
    console.log("Watch action parsed:");
    console.log("- Type:", type);
    console.log("- URL:", url);
    console.log("- Title:", title);
    console.log("- Image:", image);
    
    if (type == "movie") {
      // Авторизация уже проверена выше через forceValidateAccountOnAction()
      
      // --- НАСТРОЙКА ТРЕКЕРА ВРЕМЕНИ ПРОСМОТРА ---
      const contentId = `${type}_${url}_${title}`.replace(/[^a-zA-Z0-9_]/g, '_');
      const watchTimeTracker = createWatchTimeTracker(contentId);
      setGlobalWatchTimeTracker(watchTimeTracker);
      
      // Настраиваем обработчик для отправки аналитики при закрытии страницы
      const cleanupBeforeUnload = setupBeforeUnloadHandler(() => {
        sendPlayerAnalytics({
          type,
          title,
          imdbid: url,
          userid: localStorage.getItem("screenify:userId") || "unknown",
          audiolang: "unknown",
          subslang: "unknown",
          watchtime: watchTimeTracker.getWatchTimeInMinutes(),
          userRegion: getRegionByDomain(),
          userCountry: "unknown",
          useBeacon: true,
        });
      });
      
      // Запускаем отслеживание времени
      watchTimeTracker.start();
      
      // --- АНАЛИТИКА ЗАПУСКА ---
      sendPlayerAnalytics({
        type,
        title,
        imdbid: url, // URL может содержать imdbid
        userid: localStorage.getItem("screenify:userId") || "unknown",
        audiolang: "unknown",
        subslang: "unknown",
        watchtime: "0", // При запуске время = 0
        userRegion: getRegionByDomain(),
        userCountry: "unknown",
      });
      // --- КОНЕЦ АНАЛИТИКИ ---
      
      const imageParam = image && image.length > 0 ? `&imageUrl=${image}` : "";
      callCallback(
        {
          url: `${url}${imageParam}`,
          label: title.toUpperCase(),
          properties: playerProperties,
        },
        callback
      );
    } else if (type == "live") {
      console.log("=== PROCESSING LIVE TYPE ===");
      // Обработка ТВ каналов (live streams)
      // Авторизация уже проверена выше через forceValidateAccountOnAction()
      
      console.log("User is authenticated, proceeding with live stream");
      
      // --- НАСТРОЙКА ТРЕКЕРА ВРЕМЕНИ ПРОСМОТРА ДЛЯ LIVE ---
      const contentId = `live_${url}_${title}`.replace(/[^a-zA-Z0-9_]/g, '_');
      const watchTimeTracker = createWatchTimeTracker(contentId);
      setGlobalWatchTimeTracker(watchTimeTracker);
      
      // Настраиваем обработчик для отправки аналитики при закрытии страницы
      const cleanupBeforeUnload = setupBeforeUnloadHandler(() => {
        sendPlayerAnalytics({
          type: "live",
          title,
          imdbid: url,
          userid: localStorage.getItem("screenify:userId") || "unknown",
          audiolang: "unknown",
          subslang: "unknown",
          watchtime: watchTimeTracker.getWatchTimeInMinutes(),
          userRegion: getRegionByDomain(),
          userCountry: "unknown",
          useBeacon: true,
        });
      });
      
      // Запускаем отслеживание времени
      watchTimeTracker.start();
      
      // --- АНАЛИТИКА ЗАПУСКА LIVE ---
      sendPlayerAnalytics({
        type: "live",
        title,
        imdbid: url, // URL может содержать channel id
        userid: localStorage.getItem("screenify:userId") || "unknown",
        audiolang: "unknown",
        subslang: "unknown",
        watchtime: "0", // При запуске время = 0
        userRegion: getRegionByDomain(),
        userCountry: "unknown",
      });
      // --- КОНЕЦ АНАЛИТИКИ ---
      
      const imageParam = image && image.length > 0 ? `&imageUrl=${image}` : "";
      const finalUrl = `${url}${imageParam}`;
      
      console.log("Final URL:", finalUrl);
      console.log("Player properties:", playerProperties);
      
      callCallback(
        {
          url: finalUrl,
          label: title.toUpperCase(),
          properties: playerProperties, // Используем те же свойства плеера, что и для фильмов
        },
        callback
      );
      
      console.log("=== LIVE TYPE PROCESSING COMPLETED ===");
    }
  }
}

function completeError(error: string): string {
  if (tvx.Tools.isFullStr(error)) {
    if (error.lastIndexOf(".") != error.length - 1) {
      return error + ".";
    }
    return error;
  }
  return "";
}

export function createContentNotFound(contentId: string): tvx.MSXContentRoot {
  return {
    type: "pages",
    headline: "Warnung",
    pages: [
      {
        items: [
          {
            type: "default",
            layout: "0,0,12,6",
            headline: "{ico:msx-yellow:warning} Inhalt nicht gefunden",
            text:
              "Der Inhalt mit der ID {col:msx-white}'" +
              contentId +
              "'{col} konnte nicht gefunden werden.",
            action: "back",
          },
        ],
      },
    ],
  };
}

export function createContentLoadError(
  contentId: string,
  error: string
): tvx.MSXContentRoot {
  return {
    type: "pages",
    headline: "Fehler",
    pages: [
      {
        items: [
          {
            type: "default",
            layout: "0,0,12,6",
            headline: "{ico:msx-red:error} Inhalt konnte nicht geladen werden",
            text:
              "Der Inhalt mit der ID {col:msx-white}'" +
              contentId +
              "'{col} konnte nicht geladen werden.{br}{br}" +
              completeError(error),
            action: "back",
          },
        ],
      },
    ],
  };
}

export function createVideoLoadError(videoId: string, error: string): any {
  return {
    error:
      "Das Video mit der ID {col:msx-white}'" +
      videoId +
      "'{col} konnte nicht geladen werden.{br}" +
      completeError(error),
  };
}
export function truncateString(str: string, maxLength: number) {
  if (str.length <= maxLength) {
    return str;
  }

  const truncated = str
    .slice(0, maxLength + 1)
    .split(" ")
    .slice(0, -1)
    .join(" ");

  return truncated + "...";
}

export function getDisplayedItems<T>(
  arr: T[],
  oldIndex: number,
  direction: number
): { items: T[]; newIndex: number } {
  let newIndex = (oldIndex + direction * 5 + arr.length) % arr.length;

  if (newIndex < 0) {
    newIndex += arr.length;
  }

  let result: T[] = [];
  for (let i = 0; i < 5; i++) {
    result.push(arr[(newIndex + i) % arr.length]);
  }

  return { items: result, newIndex };
}
export function getCurrentlyDisplayingItemByIndex<T>(
  arr: T[],
  listIndex: number,
  itemIndex: number
): T {
  let result: T[] = [];
  for (let i = 0; i < 5; i++) {
    result.push(arr[(listIndex + i) % arr.length]);
  }

  return result[itemIndex - 1];
}

function comingSoonRoot(): tvx.MSXContentRoot {
  return {
    type: "list",
    headline: res_coming_soon().toUpperCase(),
    background: res_backgroundJPG,
    flag: "soon",
    important: true,
    pages: [
      {
        items: [
          {
            type: "default",
            layout: "0,0,12,1",
            title: res_coming_soon(),
            centration: "title",
            alignment: "center",
          },
        ],
      },
    ],
  };
}
export function checkInternetRoot(): tvx.MSXContentRoot {
  return {
    type: "list",
    headline: res_check_internet_headline().toUpperCase(),
    background: res_backgroundJPG,
    flag: "check",
    important: true,
    pages: [
      {
        items: [
          {
            type: "default",
            layout: "0,0,12,1",
            title: res_check_internet(),
            centration: "title",
            alignment: "center",
          },
        ],
      },
    ],
  };
}

export function createStudioRoot(studioName: string, callback: (data: any) => void): void {
  tvx.InteractionPlugin.stopLoading();
  
  console.log(`Creating studio root for: ${studioName}`);
  
  // Сначала получаем список всех студий из API
  apiGetMainStudios().then((studiosList) => {
    console.log(`Main studios list:`, studiosList);
    
    if (!studiosList || !Array.isArray(studiosList)) {
      console.error("Failed to get studios list");
      const emptyRoot: tvx.MSXContentRoot = {
        type: "list",
        headline: `${studioName.toUpperCase()} Movies & Series`,
        flag: `studio-${studioName}`,
        important: true,
        pages: [
          {
            items: [
              {
                type: "default",
                layout: "0,0,12,6",
                title: `Failed to load studios list`,
                centration: "title",
                alignment: "center",
              },
            ],
          },
        ],
      };
      callCallback(emptyRoot, callback);
      return;
    }
    
    // Ищем студию по названию
    const studio = studiosList.find((s: any) => 
      s.studioName === studioName || 
      s.studioName.toLowerCase() === studioName.toLowerCase()
    );
    
    if (!studio) {
      console.error(`Studio "${studioName}" not found in API`);
      const emptyRoot: tvx.MSXContentRoot = {
        type: "list",
        headline: `${studioName.toUpperCase()} Movies & Series`,
        flag: `studio-${studioName}`,
        important: true,
        pages: [
          {
            items: [
              {
                type: "default",
                layout: "0,0,12,6",
                title: `Studio "${studioName}" not found`,
                centration: "title",
                alignment: "center",
              },
            ],
          },
        ],
      };
      callCallback(emptyRoot, callback);
      return;
    }
    
    console.log(`Found studio:`, studio);
    
    // Получаем контент студии через API
    getStudioContent(studio.imdbid).then((studioData) => {
      console.log(`Studio data for ${studioName}:`, studioData);
      console.log(`Studio data structure:`, {
        hasStudiosFictions: !!studioData?.studiosFictions,
        studiosFictionsLength: studioData?.studiosFictions?.length,
        firstItem: studioData?.studiosFictions?.[0],
        studioDataKeys: studioData ? Object.keys(studioData) : 'no data'
      });
      
      if (studioData && studioData.studiosFictions && studioData.studiosFictions.length > 0) {
        // Создаем страницу студии аналогично createCollectionRow
        const studioPage = createStudioPage(studioData, studioName);
        
        const studioRoot: tvx.MSXContentRoot = {
          cache: true,
          restore: true,
          reuse: true,
          type: "list",
          headline: `${studioData.studioName.toUpperCase()} Movies & Series`,
          flag: `studio-${studioName}`,
          important: true,
          pages: [studioPage],
        };
        
        callCallback(studioRoot, callback);
      } else {
        // Если нет контента, показываем сообщение
        const emptyRoot: tvx.MSXContentRoot = {
          type: "list",
          headline: `${studioName.toUpperCase()} Movies & Series`,
          flag: `studio-${studioName}`,
          important: true,
          pages: [
            {
              items: [
                {
                  type: "default",
                  layout: "0,0,12,6",
                  title: `No content found for ${studioName}`,
                  centration: "title",
                  alignment: "center",
                },
              ],
            },
          ],
        };
        callCallback(emptyRoot, callback);
      }
    }).catch((error) => {
      console.error(`Error loading studio content for ${studioName}:`, error);
      stopLoadingShowError({}, callback);
    });
    
  }).catch((error) => {
    console.error(`Error loading studios list:`, error);
    stopLoadingShowError({}, callback);
  });
}

function createStudioPage(studioData: any, studioName: string): tvx.MSXContentPage {
  const studioPage: tvx.MSXContentPage = {
    headline: studioData.studioName.toUpperCase(),
    items: [],
  };
  
  // Создаем безопасный ID для студии (убираем пробелы и специальные символы)
  const safeStudioId = studioName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  
  // Добавляем элементы студии аналогично createCollectionRow
  for (let i = 0; i < Math.min(5, studioData.studiosFictions.length); i++) {
    const fiction = studioData.studiosFictions[i];
    const content = fiction.movie || fiction.serial;
    const type = fiction.movie ? "movie" : "serial";
    
    studioPage.items.push({
      id: `studio-${safeStudioId}-${i + 1}`,
      type: "separate",
      layout: `${i * 2 + 1},0,2,4`,
      image: content.portraitImage,
      title: content.title.toUpperCase(),
      action: `content:request:interaction:content:fiction:${type}:${content.imdbid}`,
    });
  }
  
  return studioPage;
}
